"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { get as idbGet, set as idbSet } from "idb-keyval";
import { searchChatGifs } from "@/actions/gifs";
import type { KlipyItem, KlipyKind, KlipyPage } from "@/lib/klipy";

const RECENT_GIFS_KEY = "chat-gif-recents";
const MAX_RECENT_GIFS = 24;

export type { KlipyItem };

export function useGifSearch(kind: KlipyKind, query: string) {
  const normalized = query.trim();

  return useInfiniteQuery({
    queryKey: ["chat-gifs", kind, normalized],
    queryFn: async ({ pageParam }) => {
      const result = await searchChatGifs({
        kind,
        query: normalized || undefined,
        page: pageParam,
      });
      if (result.error || !result.data) {
        throw new Error(result.error ?? "Failed to load GIFs");
      }
      return result.data as KlipyPage;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.page + 1 : undefined,
    staleTime: 60_000,
  });
}

export function useRecentGifs() {
  return useQuery({
    queryKey: ["chat-gifs", "recents"],
    queryFn: async () => (await idbGet<KlipyItem[]>(RECENT_GIFS_KEY)) ?? [],
    staleTime: 0,
  });
}

export async function pushRecentGif(item: KlipyItem) {
  const existing = (await idbGet<KlipyItem[]>(RECENT_GIFS_KEY)) ?? [];
  const next = [
    item,
    ...existing.filter((entry) => entry.slug !== item.slug),
  ].slice(0, MAX_RECENT_GIFS);
  await idbSet(RECENT_GIFS_KEY, next);
}
